import React from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';

import { ErrorIcon } from '../../icons/ErrorIcon';
import { Info as InfoIcon } from '../../icons/Info';
import { Present as PresentIcon } from '../../icons/Present';
import { Success as SuccessIcon } from '../../icons/Success';
import { Warning as WarningIcon } from '../../icons/Warning';
import { Typography } from '../Typography';

export type Variant =
  | 'error'
  | 'info'
  | 'reward'
  | 'success'
  | 'warning'
  | 'notice';

export interface SnackbarProps {
  button?: JSX.Element;
  className?: string;
  dataFd?: string;
  isNotification?: boolean;
  message: JSX.Element[] | string;
  size?: 'xs' | 's';
  type?: 'primary' | 'secondary';
  variant?: Variant;
}

const useStyles = makeStyles((theme: Theme) => ({
  smallFont: {
    fontSize: theme.typography.pxToRem(13),
  },
  smallSvgMargin: {
    '& svg': {
      marginRight: theme.spacing(1),
    },
  },

  snackbar: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    wordBreak: 'break-word',

    '& .fd-text': {
      marginBottom: 0,
    },

    '& svg': {
      color: theme.palette.common.white,
    },

    '&.advice': {
      backgroundColor: 'transparent',

      '& .fd-text': {
        color: theme.palette.text.primary,
      },

      '& .fd-icon__info': {
        color: theme.palette.info.main,
      },
    },

    '&.error': {
      backgroundColor: theme.palette.error.main,

      '& .fd-text': {
        color: theme.palette.error.contrastText,
      },
    },

    '&.info': {
      backgroundColor: theme.palette.info.main,

      '& .fd-text': {
        color: theme.palette.info.contrastText,
      },
    },

    '&.notice': {
      backgroundColor: theme.palette.common.black,

      '& .fd-text': {
        color: theme.palette.common.white,
      },
    },

    '&.success': {
      backgroundColor: theme.palette.success.main,

      '& .fd-text': {
        color: theme.palette.success.contrastText,
      },
    },

    '&.warning': {
      backgroundColor: theme.palette.warning.main,

      '& .fd-text': {
        color: theme.palette.warning.contrastText,
      },
    },
  },

  notificationSnackbar: {
    height: '52px',
    marginBottom: theme.spacing(0),
    padding: theme.spacing(0.75, 2.5),
  },

  container: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },

  secondarySnackbar: {
    alignItems: 'center',
    display: 'flex',
    wordBreak: 'break-word',

    '& .fd-text': {
      marginBottom: 0,
    },

    '&.error': {
      '& .fd-text, svg': {
        color: theme.palette.error.main,
      },
    },

    '&.info': {
      '& .fd-text': {
        color: theme.palette.common.black,
      },
      '& svg': {
        color: theme.palette.info.main,
      },
    },

    '&.notice': {
      '& .fd-text, svg': {
        color: theme.palette.common.black,
      },
    },

    '&.success': {
      '& .fd-text, svg': {
        color: theme.palette.success.main,
      },
    },

    '&.warning': {
      '& .fd-text, svg': {
        color: theme.palette.warning.main,
      },
    },
  },

  svgMargin: {
    '& svg': {
      marginRight: theme.spacing(1.5),
    },
  },

  [theme.breakpoints.up('sm')]: {
    notificationSnackbar: {
      width: '288px',
    },
  },
}));

export function Snackbar(props: SnackbarProps): JSX.Element {
  const {
    button = null,
    className = '',
    dataFd = 'noticebar',
    isNotification,
    message,
    size = 's',
    type = 'primary',
    variant = 'info',
  } = props;
  const classes = useStyles();

  let snackIcon: JSX.Element | null = null;
  const fontStyle = size === 'xs' ? classes.smallFont : '';

  switch (variant) {
    case 'error':
      snackIcon = <ErrorIcon size={size} />;
      break;
    case 'info':
      snackIcon = <InfoIcon size={size} />;
      break;
    case 'reward':
      snackIcon = <PresentIcon size={size} />;
      break;
    case 'success':
      snackIcon = <SuccessIcon size={size} />;
      break;
    case 'warning':
      snackIcon = <WarningIcon size={size} />;
      break;
    default:
      snackIcon = null;
  }

  const snackBarStyles = clsx(
    size === 'xs' ? classes.smallSvgMargin : classes.svgMargin,
    type === 'primary' ? classes.snackbar : classes.secondarySnackbar,
    'fd-noticebar',
    `fd-noticebar__${variant}`,
    isNotification && classes.notificationSnackbar,
    variant === 'error' && 'error',
    variant === 'info' && 'info',
    variant === 'notice' && 'notice',
    variant === 'reward' && 'success',
    variant === 'success' && 'success',
    variant === 'warning' && 'warning',
    className || '',
  );

  return (
    <div aria-live="polite" className={snackBarStyles} data-fd={dataFd}>
      <div className={classes.container}>
        {snackIcon}
        <Typography className={fontStyle}>{message}</Typography>
      </div>

      {button}
    </div>
  );
}
